/**
 * API client for YUL ON TAP platform.
 * 
 * OS-Style Pattern: Centralized API calls.
 * Phase 2: Added engagement APIs
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create public client (no auth required for guest flows)
const publicClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || localStorage.getItem('hotels_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth API
export const authAPI = {
  login: (email, password, rememberMe = false) => 
    apiClient.post('/auth/login', { email, password, remember_me: rememberMe }),
  register: (data) => apiClient.post('/auth/register', data),
  me: () => apiClient.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    apiClient.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// SSO API - Restaurant System Bridge
export const ssoAPI = {
  getToken: (industry) => apiClient.post('/auth/sso-token', { industry }),
  getConfig: () => apiClient.get('/auth/sso-config'),
  validate: (token) => publicClient.get(`/auth/sso-validate?token=${token}`),
};

// Admin API (Super Admin only)
export const adminAPI = {
  listUsers: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.industry) params.append('industry', filters.industry);
    return apiClient.get(`/admin/users?${params.toString()}`);
  },
  getUser: (userId) => apiClient.get(`/admin/users/${userId}`),
  createUser: (userData) => apiClient.post('/admin/users', userData),
  deleteUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  updateUserStatus: (userId, status) => apiClient.put(`/admin/users/${userId}/status`, { status }),
  forcePasswordReset: (userId) => apiClient.post(`/admin/users/${userId}/force-reset`),
  generateResetLink: (userId) => apiClient.post(`/admin/users/${userId}/generate-reset-link`),
  invalidateSessions: (userId) => apiClient.post(`/admin/users/${userId}/invalidate-sessions`),
  getAuditLogs: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.userId) params.append('user_id', filters.userId);
    if (filters.action) params.append('action', filters.action);
    if (filters.limit) params.append('limit', filters.limit.toString());
    return apiClient.get(`/admin/audit-logs?${params.toString()}`);
  },
};

// Platform Values API (Super Admin configurable settings)
export const platformAPI = {
  getValues: () => apiClient.get('/platform/values'),
  updateValues: (updates) => apiClient.put('/platform/values', updates),
  updateSingleValue: (key, value, description) => 
    apiClient.put(`/platform/values/${key}`, { key, value, description }),
  resetToDefaults: () => apiClient.post('/platform/values/reset'),
};

// Hotels API (OS-Style: mirrors restaurants API)
export const hotelsAPI = {
  list: () => apiClient.get('/hotels'),
  get: (id) => apiClient.get(`/hotels/${id}`),
  create: (data) => apiClient.post('/hotels', data),
  update: (id, data) => apiClient.put(`/hotels/${id}`, data),
  delete: (id) => apiClient.delete(`/hotels/${id}`),
  getStats: (id) => apiClient.get(`/hotels/${id}/stats`),
  getEngagementStats: (id) => apiClient.get(`/hotels/${id}/engagement-stats`),
  getReviews: (id) => apiClient.get(`/hotels/${id}/reviews`),
  getSurveys: (id) => apiClient.get(`/hotels/${id}/surveys`),
  // Tap Engine URL Configuration
  getTapEngineUrls: (id) => apiClient.get(`/hotels/${id}/tap-engine-urls`),
  updateTapEngineUrls: (id, data) => apiClient.put(`/hotels/${id}/tap-engine-urls`, data),
  // Hotel Users (for alert assignment)
  getUsers: (id) => apiClient.get(`/hotels/${id}/users`),
};

// Hotel Groups API (Multi-property management)
export const hotelGroupsAPI = {
  list: () => apiClient.get('/hotel-groups'),
  get: (id) => apiClient.get(`/hotel-groups/${id}`),
  create: (data) => apiClient.post('/hotel-groups', data),
  update: (id, data) => apiClient.put(`/hotel-groups/${id}`, data),
  delete: (id) => apiClient.delete(`/hotel-groups/${id}`),
  getStats: (id, days = 7) => apiClient.get(`/hotel-groups/${id}/stats`, { params: { days } }),
  getUsers: (id) => apiClient.get(`/hotel-groups/${id}/users`),
  addHotel: (groupId, hotelId) => apiClient.post(`/hotel-groups/${groupId}/hotels/${hotelId}`),
  removeHotel: (groupId, hotelId) => apiClient.delete(`/hotel-groups/${groupId}/hotels/${hotelId}`),
};

// Rooms API (OS-Style: mirrors tables API)
export const roomsAPI = {
  list: (hotelId) => apiClient.get(`/hotels/${hotelId}/rooms`),
  create: (hotelId, data) => apiClient.post(`/hotels/${hotelId}/rooms`, data),
  update: (hotelId, roomId, data) => apiClient.put(`/hotels/${hotelId}/rooms/${roomId}`, data),
  delete: (hotelId, roomId) => apiClient.delete(`/hotels/${hotelId}/rooms/${roomId}`),
  assignTag: (hotelId, roomId, tagId, engagementType, alertUserId = null, alertEmail = null, tagLabel = null) => 
    apiClient.post(`/hotels/${hotelId}/rooms/${roomId}/assign-tag`, { 
      tag_id: tagId, 
      engagement_type: engagementType,
      alert_user_id: alertUserId,
      alert_email: alertEmail,
      tag_label: tagLabel
    }),
  unassignTag: (hotelId, roomId) => apiClient.delete(`/hotels/${hotelId}/rooms/${roomId}/unassign-tag`),
};

// Dashboard API
export const dashboardAPI = {
  getVerticals: () => apiClient.get('/verticals'),
  getIndustries: () => publicClient.get('/industries'),
  getHotelStats: () => apiClient.get('/dashboard/hotel-stats'),
};

// Comparison Analytics API (Multi-property)
export const comparisonAPI = {
  getAccessibleHotels: () => apiClient.get('/comparison/accessible-hotels'),
  compareProperties: (hotelIds, days = 30, engagementType = 'combined') => 
    apiClient.post('/comparison/analytics', hotelIds, { 
      params: { days, engagement_type: engagementType } 
    }),
};

// Engagement API (Phase 2)
export const engagementAPI = {
  // Config
  getTypes: () => publicClient.get('/engagement-types'),
  getSurveyQuestions: () => publicClient.get('/survey-questions'),
  
  // Guest flows (public - no auth)
  getReviewPageData: (hotelId, roomId, tagId) => 
    publicClient.get(`/review/${hotelId}/${roomId}`, { params: { tag_id: tagId } }),
  submitReview: (hotelId, roomId, data, tagId) => 
    publicClient.post(`/review/${hotelId}/${roomId}`, data, { params: { tag_id: tagId } }),
  
  getSurveyPageData: (hotelId, roomId, tagId, lang = 'en') => 
    publicClient.get(`/survey/${hotelId}/${roomId}`, { params: { tag_id: tagId, lang } }),
  submitSurvey: (hotelId, roomId, data, tagId) => 
    publicClient.post(`/survey/${hotelId}/${roomId}`, data, { params: { tag_id: tagId } }),
  submitDynamicSurvey: (hotelId, roomId, data, tagId) => 
    publicClient.post(`/survey/${hotelId}/${roomId}/dynamic`, data, { params: { tag_id: tagId } }),
  
  // Tag resolution
  resolveTag: (tagId) => publicClient.get(`/t/${tagId}`),
  
  // Tag lookup (new endpoint for NFC tap flow)
  lookupTag: (tagId) => publicClient.get(`/tap/${tagId}`),
};

// Survey Config API (Phase 3.5)
export const surveyConfigAPI = {
  getConfig: (hotelId) => apiClient.get(`/hotels/${hotelId}/survey-config`),
  updateConfig: (hotelId, questions) => apiClient.put(`/hotels/${hotelId}/survey-config`, questions),
  reorderQuestions: (hotelId, questionIds) => 
    apiClient.put(`/hotels/${hotelId}/survey-config/reorder`, { question_ids: questionIds }),
  addQuestion: (hotelId, data) => apiClient.post(`/hotels/${hotelId}/survey-config/questions`, data),
  updateQuestion: (hotelId, questionId, data) => 
    apiClient.put(`/hotels/${hotelId}/survey-config/questions/${questionId}`, data),
  deleteQuestion: (hotelId, questionId) => 
    apiClient.delete(`/hotels/${hotelId}/survey-config/questions/${questionId}`),
};

// Global Survey Config API (Super Admin only)
export const globalSurveyConfigAPI = {
  getConfig: () => apiClient.get('/admin/survey-config'),
  updateQuestion: (questionId, data) => 
    apiClient.put(`/admin/survey-config/questions/${questionId}`, data),
  reorderQuestions: (questionIds) => 
    apiClient.put('/admin/survey-config/reorder', { question_ids: questionIds }),
  addQuestion: (data) => apiClient.post('/admin/survey-config/questions', data),
  deleteQuestion: (questionId) => 
    apiClient.delete(`/admin/survey-config/questions/${questionId}`),
  applyToAllHotels: () => apiClient.post('/admin/survey-config/apply-to-all'),
};

// Help Center API
export const helpCenterAPI = {
  getArticles: (search = null, category = null, lang = null) => 
    apiClient.get('/help/articles', { params: { search, category, lang } }),
  getArticle: (articleId) => apiClient.get(`/help/articles/${articleId}`),
  getCategories: () => apiClient.get('/help/categories'),
  // Admin only
  createArticle: (data) => apiClient.post('/help/articles', data),
  updateArticle: (articleId, data) => apiClient.put(`/help/articles/${articleId}`, data),
  deleteArticle: (articleId) => apiClient.delete(`/help/articles/${articleId}`),
};

// Analytics API (Phase 3)
export const analyticsAPI = {
  getOverview: (hotelId, days = 30) => 
    apiClient.get(`/hotels/${hotelId}/analytics/overview`, { params: { days } }),
  getTrends: (hotelId, days = 30, engagementType = null) => 
    apiClient.get(`/hotels/${hotelId}/analytics/trends`, { params: { days, engagement_type: engagementType } }),
  getRoomAnalytics: (hotelId, engagementType = null) => 
    apiClient.get(`/hotels/${hotelId}/analytics/rooms`, { params: { engagement_type: engagementType } }),
  exportCSV: (hotelId, engagementType = 'combined', lang = 'en') => 
    apiClient.get(`/hotels/${hotelId}/export/csv`, { params: { engagement_type: engagementType, lang }, responseType: 'blob' }),
  getReportData: (hotelId, days = 30, lang = 'en') => 
    apiClient.get(`/hotels/${hotelId}/export/report`, { params: { days, lang } }),
};

// Incident Data & Analytics API
export const incidentAPI = {
  backfill: () => apiClient.post('/incidents/backfill'),
  list: (params = {}) => apiClient.get('/incidents/list', { params }),
  getDetail: (incidentId) => apiClient.get(`/incidents/detail/${incidentId}`),
  getDashboard: (orgIds = null) => apiClient.get('/incidents/dashboard', { params: orgIds ? { org_ids: orgIds } : {} }),
  getAnalytics: (params = {}) => apiClient.get('/incidents/analytics', { params }),
  getSafetyScores: (orgIds = null, days = 30) => apiClient.get('/incidents/safety-scores', { params: { ...(orgIds ? { org_ids: orgIds } : {}), days } }),
  getTodayCount: () => apiClient.get('/incidents/today-count'),
  exportCSV: (params = {}) => apiClient.get('/incidents/export/csv', { params, responseType: 'blob' }),
};
